import React from 'react';
import { useLocation } from 'react-router-dom';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ToolbarPopover from 'components/layout/ToolbarPopover';

type GuideEntry = {
  /** ガイドページの表示タイトル（ユースケース名） */
  title: string;
  /** ガイドページのパス（guide/以降の部分） */
  path: string;
};

/**
 * 画面URLと関連するガイドページの1対多マッピング。
 * キーの path はルートからの相対パス（前方一致で判定）。
 * タイトルは各ガイドページのユースケース名を使用する。
 */
const GUIDE_MAP: { path: string; guides: GuideEntry[] }[] = [
  {
    path: '/management/user',
    guides: [
      { title: '新規ユーザーを登録して権限を付与する', path: '/guide/user/register' },
      { title: 'ユーザー情報を変更する', path: '/guide/user/update' },
      { title: 'ユーザーを削除する', path: '/guide/user/delete' },
      { title: 'ユーザーの権限を変更する', path: '/guide/user/role' },
      { title: 'ユーザーを検索する', path: '/guide/user/search' },
    ],
  },
  {
    path: '/management/notice',
    guides: [
      { title: '特定ユーザーに通知を送る', path: '/guide/notice/create-notice' },
      { title: '全ユーザーにお知らせを配信する', path: '/guide/notice/create-info' },
      { title: '通知を管理する（編集・削除・解決・検索）', path: '/guide/notice/admin-manage' },
      { title: '通知を確認・操作する（ユーザー）', path: '/guide/notice/user' },
    ],
  },
  {
    path: '/management/financial-year',
    guides: [{ title: '年度を管理する', path: '/guide/financial-year/register' }],
  },
  {
    path: '/member/partner',
    guides: [
      { title: '新規パートナーを登録する', path: '/guide/partner/register' },
      { title: 'パートナーを一括登録する', path: '/guide/partner/bulk-register' },
      { title: 'パートナー情報を更新する', path: '/guide/partner/update' },
      { title: 'パートナーの詳細情報を確認する', path: '/guide/partner/detail' },
      { title: 'パートナーを検索する', path: '/guide/partner/search' },
    ],
  },
  {
    path: '/member/client',
    guides: [
      { title: '新規取引先を登録する', path: '/guide/client/register' },
      { title: '取引先を一括登録する', path: '/guide/client/bulk-register' },
      { title: '取引先情報を更新する', path: '/guide/client/update' },
      { title: '取引先の詳細情報を確認する', path: '/guide/client/detail' },
      { title: '取引先を検索する', path: '/guide/client/search' },
    ],
  },
  {
    path: '/member/project',
    guides: [
      { title: '新規プロジェクトを登録する', path: '/guide/project/register' },
      { title: 'プロジェクトを一括登録する', path: '/guide/project/bulk-register' },
      { title: 'プロジェクト情報を更新する', path: '/guide/project/update' },
      { title: 'プロジェクトの詳細情報を確認する', path: '/guide/project/detail' },
      { title: 'プロジェクトを検索する', path: '/guide/project/search' },
    ],
  },
  {
    path: '/member/member-mgmt',
    guides: [
      { title: '社員を登録する', path: '/guide/member/register-employee' },
      { title: '社外要員を登録する', path: '/guide/member/register-external' },
      { title: '要員を一括登録する', path: '/guide/member/bulk-register' },
      { title: '要員情報を更新する', path: '/guide/member/update' },
      { title: '要員を削除する', path: '/guide/member/delete' },
      { title: '要員を検索する', path: '/guide/member/search' },
      { title: '要員の詳細情報を確認する', path: '/guide/member/detail' },
    ],
  },
  {
    path: '/contract/request',
    guides: [
      { title: '新規契約を申請する', path: '/guide/contract/new-request' },
      { title: '契約内容の変更・解除を申請する', path: '/guide/contract/change-terminate' },
      { title: '契約申請の修正・対応・取消を行う', path: '/guide/contract/request-manage' },
      { title: '契約申請にコメントする', path: '/guide/contract/comment' },
      { title: '契約申請を複製する', path: '/guide/contract/duplicate' },
      { title: '契約・契約申請を検索する', path: '/guide/contract/search' },
    ],
  },
  {
    path: '/contract/contract-request-create',
    guides: [
      { title: '新規契約を申請する', path: '/guide/contract/new-request' },
      { title: '契約内容の変更・解除を申請する', path: '/guide/contract/change-terminate' },
      { title: 'チェックリストの操作・管理', path: '/guide/contract/checklist' },
    ],
  },
  {
    path: '/contract/contract-list',
    guides: [
      { title: '契約・契約申請を検索する', path: '/guide/contract/search' },
      { title: '契約申請にコメントする', path: '/guide/contract/comment' },
    ],
  },
  {
    path: '/contract/checklist',
    guides: [{ title: 'チェックリストの操作・管理', path: '/guide/contract/checklist' }],
  },
  {
    path: '/contract/template',
    guides: [{ title: 'テンプレート（自由項目）を管理する', path: '/guide/contract/template' }],
  },
  {
    path: '/assessment',
    guides: [
      { title: '資産を登録する', path: '/guide/assessment/register' },
      { title: '資産を一括登録する', path: '/guide/assessment/bulk-register' },
      { title: '資産情報を更新する', path: '/guide/assessment/update' },
      { title: '資産を除却する', path: '/guide/assessment/dispose' },
      { title: '資産の詳細情報を確認する', path: '/guide/assessment/detail' },
      { title: '資産を検索する', path: '/guide/assessment/search' },
    ],
  },
  {
    path: '/security',
    guides: [
      { title: 'セキュリティチェックを管理する（管理者）', path: '/guide/security/admin-start' },
      { title: 'セキュリティチェックを実施する', path: '/guide/security/user-execute' },
    ],
  },
];

/**
 * 現在のURLから関連するガイドページ一覧を解決する。
 * マッチしない場合は空配列を返す。
 */
const resolveGuideEntries = (pathname: string): GuideEntry[] => {
  for (const entry of GUIDE_MAP) {
    if (pathname === entry.path || pathname.startsWith(`${entry.path}/`)) {
      return entry.guides;
    }
  }

  return [];
};

/**
 * ヘッダーに表示するヒントアイコン。
 * クリックすると現在の画面に関連するガイドページの一覧をポップアップで表示する。
 * ポップアップから選択すると別タブでガイドページを開く。
 * 関連するガイドがない場合はガイドTOPを別タブで開く。
 */
const HelpLink: React.FC = () => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const guides = resolveGuideEntries(location.pathname);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (guides.length === 0) {
      // マッチしない場合はガイドTOPを直接開く
      window.open('/guide', '_blank', 'noopener,noreferrer');
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleGuideClick = (guidePath: string) => {
    window.open(guidePath, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  return (
    <>
      <Tooltip title="利用ガイド">
        <IconButton size="small" onClick={handleClick}>
          <HelpOutlineIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        </IconButton>
      </Tooltip>
      <ToolbarPopover open={open} anchorEl={anchorEl} onClose={handleClose} width={320}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            関連する利用ガイド
          </Typography>
        </Box>
        <List disablePadding>
          {guides.map((guide) => (
            <ListItemButton
              key={guide.path}
              onClick={() => handleGuideClick(guide.path)}
              sx={{
                py: 1,
                px: 2,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemText primary={guide.title} primaryTypographyProps={{ variant: 'body2' }} />
              <OpenInNewIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 1 }} />
            </ListItemButton>
          ))}
        </List>
      </ToolbarPopover>
    </>
  );
};

export default HelpLink;
